import { useTranslation } from 'next-i18next/pages';
import React from 'react';

import Area from '@/components/apps/layouts/shared/Area';
import SectionTitle from '@/components/apps/ui/title/SectionTitle';
import { AniSlideUp } from '@/components/shared/animations/basic';

import { useIndustrialProductionMetrics } from '../useIndustrialPageData';

/**
 * ### IndustrialEvidenceSection
 *
 * States the three published Listen AI Industrial production figures, each
 * next to the conditions it was measured under.
 *
 * The scope is rendered as part of the same block as the value rather than as
 * a footnote, because an Industrial figure is measured per inspection type: a
 * value lifted away from its scope reads as a claim about the whole product.
 * Figures whose scope is not published are left out of the list entirely.
 *
 * @returns The rendered production-figures section element
 * @see {@link useIndustrialProductionMetrics} The figures rendered here
 */
export default function IndustrialEvidenceSection() {
  const { t } = useTranslation('common');
  const metrics = useIndustrialProductionMetrics(t);

  return (
    <section
      className="bg-apps-brandGrayBlack pt-[clamp(100px,10.417vw,200px)]"
      data-analytics-section="production_metrics"
    >
      <Area>
        <SectionTitle
          title={t('디플리가 제공하는 3대 양산 지표')}
          color="lighter"
        />
        {/*
          AniSlideUp 은 className 을 받지 않아 li 자체에 간격을 줄 수 없다.
          간격은 ul 이 쥐고(모바일 세로 스택 / lg 3열), 각 항목은 위쪽 구분선만
          그린다.
        */}
        <ul className="mt-[40px] flex flex-col gap-[24px] lg:mt-[clamp(40px,6.667vw,80px)] lg:grid lg:grid-cols-3 lg:gap-[40px]">
          {metrics.map((metric, index) => (
            <AniSlideUp key={metric.label} delay={0.2 * index} element="li">
              <div className="border-t border-t-apps-brandGray50 pt-[24px] lg:pt-[32px]">
                <p className="text-[clamp(40px,3.333vw,56px)] font-bold leading-[120%] tracking-[-1.12px] text-apps-brandGrayWhite">
                  {metric.value}
                </p>
                <h3 className="mt-[12px] text-[20px] font-bold leading-[140%] tracking-[-0.4px] text-apps-coreGrayWhite lg:text-[22px] lg:tracking-[-0.44px]">
                  {metric.label}
                </h3>
                <p className="mt-[8px] font-medium leading-[140%] tracking-[-0.32px] text-apps-brandGray600 lg:text-[18px] lg:leading-[160%] lg:tracking-[-0.36px]">
                  {metric.scope}
                </p>
              </div>
            </AniSlideUp>
          ))}
        </ul>
      </Area>
    </section>
  );
}
